import { capitalCase } from "change-case";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

import {
  Alert,
  FieldList,
  ProgressCircle,
  Select,
} from "@smart/itops-sb-design-system-dom";
import { Gql } from "@smart/manage-gql-operations-dom";

import { ContactSearchField } from "../../../shared";

export type CreateContact = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  contactId: string | undefined;
};

export const AddContactDetails = ({
  loading,
  control,
  setValue,
  trigger,
  isContactSelected,
  showContactTypeSelect,
  contactType,
  setContactType,
}: {
  loading: boolean;
  control: Control<CreateContact>;
  setValue: UseFormSetValue<CreateContact>;
  trigger: UseFormTrigger<CreateContact>;
  isContactSelected: boolean;
  showContactTypeSelect: boolean;
  contactType: "person" | "company";
  setContactType: (contactType: "person" | "company") => void;
}) => {
  const onSelect = (contact: Gql.ContactFieldsFragment) => {
    setValue("contactId", contact.id);
    if (contactType === "person") {
      if (contact.person?.firstName)
        setValue("firstName", contact.person.firstName);
      if (contact.person?.lastName)
        setValue("lastName", contact.person.lastName);
    } else if (contactType === "company") {
      if (contact.company?.name) setValue("companyName", contact.company.name);
    }
    trigger().catch(console.error);
  };

  const fields = (
    <FieldList className="gap-medium">
      {showContactTypeSelect && (
        <Select
          dataTestId="no-contact-modal-contact-type"
          label="Contact Type"
          options={["person", "company"].map((item) => ({
            label: capitalCase(item),
            value: item,
          }))}
          value={contactType}
          onChange={(selectedValue) => {
            setContactType(selectedValue as "person" | "company");
          }}
        />
      )}
      {contactType === "person" && (
        <>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => (
              <ContactSearchField
                contactType={contactType}
                id={field.name}
                errorId={`${field.name}-error`}
                onSelect={onSelect}
                field={{
                  uri: field.name,
                  sectionUri: "",
                  order: "a",
                  type: "text",
                  label: "First Name",
                  mandatory: true,
                }}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue("contactId", undefined);
                }}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                dataTestId="no-contact-modal-first-name"
              />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => (
              <ContactSearchField
                contactType={contactType}
                id={field.name}
                errorId={`${field.name}-error`}
                onSelect={onSelect}
                field={{
                  uri: field.name,
                  sectionUri: "",
                  order: "b",
                  type: "text",
                  label: "Last Name",
                  mandatory: true,
                }}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue("contactId", undefined);
                }}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                dataTestId="no-contact-modal-last-name"
              />
            )}
          />
        </>
      )}
      {contactType === "company" && (
        <Controller
          control={control}
          name="companyName"
          render={({ field, fieldState }) => (
            <ContactSearchField
              contactType={contactType}
              id={field.name}
              errorId={`${field.name}-error`}
              onSelect={onSelect}
              field={{
                uri: field.name,
                sectionUri: "",
                order: "c",
                type: "text",
                label: "Company Name",
                mandatory: true,
              }}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                setValue("contactId", undefined);
              }}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              dataTestId="no-contact-modal-company-name"
            />
          )}
        />
      )}
    </FieldList>
  );

  return (
    <div className="flex flex-col gap-medium">
      <div className="font-semibold text-paragraph">Add Details</div>
      {loading ? <ProgressCircle size="medium" /> : fields}
      {isContactSelected && (
        <Alert
          variant="section"
          content="Existing contact will be used to create the lead."
        />
      )}
    </div>
  );
};
